import { formatDistanceToNow, parseISO, isValid } from 'date-fns';
import clsx from 'clsx';
import { formatLabel } from '../../lib/formatLabel';

/**
 * Defensive timestamp parser. The dashboard endpoint serializes audit log
 * createdAt values as ISO strings, but QA #3 finding F observed Recent
 * Activity timestamps reading "about 10 hours ago" for events created
 * during the same session — while the Audit Logs page showed the same
 * events at the correct local time. Likely root cause: somewhere along
 * the chain a timestamp lost its trailing 'Z' and parseISO interpreted
 * it as local time instead of UTC.
 *
 * This helper:
 *   1. Tries parseISO first (the canonical path).
 *   2. If the string lacks a timezone suffix, appends 'Z' and re-parses
 *      so a timezone-naive ISO string is treated as UTC.
 *   3. Falls back to native Date as a last resort.
 *   4. Returns null on a fully unparseable input so callers can render
 *      a placeholder instead of "Invalid Date".
 */
function parseTimestamp(value: unknown): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === 'string') {
    let parsed = parseISO(value);
    if (isValid(parsed)) return parsed;

    // No timezone suffix? Treat as UTC.
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(value)) {
      parsed = parseISO(value + 'Z');
      if (isValid(parsed)) return parsed;
    }

    const native = new Date(value);
    return isValid(native) ? native : null;
  }

  return null;
}

interface Activity {
  id: string;
  action: string;
  entityType: string;
  description: string;
  timestamp: string;
  userName: string;
}

interface RecentActivityFeedProps {
  activity: Activity[];
}

function formatAction(action: string): string {
  return formatLabel(action);
}

const entityTypeColors: Record<string, string> = {
  REPORT: 'bg-blue-100 text-blue-700',
  UPLOAD: 'bg-green-100 text-green-700',
  TASK: 'bg-purple-100 text-purple-700',
  EXCEPTION: 'bg-amber-100 text-amber-700',
  EXAM: 'bg-red-100 text-red-700',
  FINDING: 'bg-orange-100 text-orange-700',
  VALIDATION: 'bg-teal-100 text-teal-700',
};

function getEntityBadgeClass(entityType: string): string {
  return entityTypeColors[entityType] || 'bg-gray-100 text-gray-700';
}

function formatEntityType(entityType: string): string {
  return formatLabel(entityType);
}

export default function RecentActivityFeed({ activity }: RecentActivityFeedProps) {
  if (!activity || activity.length === 0) {
    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <p className="text-sm text-gray-500">No recent activity</p>
        </div>
      </div>
    );
  }

  const displayActivity = activity.slice(0, 10);

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
      <div className="space-y-3">
        {displayActivity.map((item, index) => (
          <div key={item.id} className="flex gap-3">
            {/* Timeline dot and line */}
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="mt-1.5 h-2.5 w-2.5 rounded-full bg-teal-500" />
              {index < displayActivity.length - 1 && (
                <div className="w-px flex-1 bg-gray-200 mt-1" />
              )}
            </div>

            {/* Content */}
            <div className="min-w-0 pb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-900">
                  {formatAction(item.action)}
                </span>
                <span
                  className={clsx(
                    'inline-block text-xs px-1.5 py-0.5 rounded',
                    getEntityBadgeClass(item.entityType)
                  )}
                >
                  {formatEntityType(item.entityType)}
                </span>
              </div>
              {/* Only show the description subtitle when it's a real
                  title from details.title — backend falls back to the
                  raw action string when no title exists, which produced
                  noise like "ai_performance.dashboard_viewed" repeated
                  below the formatted action label. */}
              {item.description && item.description !== item.action && (
                <p className="text-sm text-gray-600 truncate">{item.description}</p>
              )}
              <p className="text-xs text-gray-400 mt-0.5">
                {item.userName ? `${item.userName} · ` : ''}
                {(() => {
                  const ts = parseTimestamp(item.timestamp);
                  return ts ? formatDistanceToNow(ts, { addSuffix: true }) : '—';
                })()}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
